/**
 * Thin API client for the new design. Same backend and the same token handling as the classic
 * screens (AuthApiService.fetchWithAuth → refresh on 401/444), but it keeps the backend error
 * envelope `{status:false, error:{code, message_code, message, details}}` so errors can be
 * localized with tError(message_code) instead of showing raw English text.
 * Every request sends the app language as Accept-Language. (Not X-User-Locale: the API's CORS policy only
 * allows Authorization, Content-Type, Accept, Accept-Language and X-Signature, so a custom header fails the
 * browser's preflight; for doctor tokens the server falls back to Accept-Language anyway.)
 */
import { useCallback } from "react";
import { AuthApiService } from "../../features/auth/api/AuthApiService";
import { useV2Lang } from "./i18n";
import { uploadProblemText } from "../../shared/utils/uploadPolicy";

export const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

export interface ApiError {
  status: number;
  message_code?: string;
  message?: string;
  details?: any;
}

export type ApiResult<T> = { ok: true; status: number; data: T; raw: any } | { ok: false; status: number; error: ApiError };

function locale(): string {
  return localStorage.getItem("app_language") === "en" ? "en" : "ar";
}

function localeHeaders(): Record<string, string> {
  const l = locale();
  return { "Accept-Language": l };
}

export function parseError(status: number, body: any): ApiError {
  if (body && typeof body === "object") {
    if (body.error && typeof body.error === "object") {
      return { status, message_code: body.error.message_code, message: body.error.message, details: body.error.details };
    }
    if (typeof body.detail === "string") return { status, message: body.detail };
    if (Array.isArray(body.detail)) {
      return { status, message_code: "validation.error", message: body.detail.map((d: any) => d?.msg).filter(Boolean).join(" · "), details: { errors: body.detail } };
    }
    if (typeof body.message === "string") return { status, message_code: body.message_code, message: body.message, details: body.details };
  }
  return { status, message: typeof body === "string" ? body : undefined };
}

async function readJson(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: any;
  /** Public endpoints (login, OTP, public verification) skip the auth header. */
  auth?: boolean;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** Unwraps `{status, data}` success envelopes; returns the raw body as well. */
export async function apiRequest<T = any>(path: string, opts: RequestOptions = {}): Promise<ApiResult<T>> {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const isForm = typeof FormData !== "undefined" && opts.body instanceof FormData;
  const init: RequestInit = {
    method: opts.method || (opts.body !== undefined ? "POST" : "GET"),
    headers: { ...(isForm ? {} : { "Content-Type": "application/json" }), ...localeHeaders(), ...(opts.headers || {}) },
    body: opts.body === undefined ? undefined : isForm ? opts.body : JSON.stringify(opts.body),
    signal: opts.signal,
  };
  try {
    const res = opts.auth === false ? await fetch(url, init) : await AuthApiService.getInstance().fetchWithAuth(url, init);
    const body = await readJson(res);
    if (!res.ok || (body && typeof body === "object" && body.status === false)) {
      return { ok: false, status: res.status, error: parseError(res.status, body) };
    }
    const data = body && typeof body === "object" && "data" in body && body.data !== undefined ? body.data : body;
    return { ok: true, status: res.status, data: data as T, raw: body };
  } catch (e: any) {
    if (e?.name === "AbortError") return { ok: false, status: 0, error: { status: 0, message_code: "aborted" } };
    return { ok: false, status: 0, error: { status: 0, message_code: "network.error", message: e?.message } };
  }
}

/**
 * Multipart upload with progress (XHR), auth header, one refresh-and-retry on 401/444.
 * Returns an abort function through `onAbortReady`.
 */
export function uploadWithProgress<T = any>(
  path: string,
  form: FormData,
  onProgress?: (pct: number) => void,
  onAbortReady?: (abort: () => void) => void
): Promise<ApiResult<T>> {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const authApi = AuthApiService.getInstance();
  let sentToken: string | null = null;
  const attempt = (retried: boolean): Promise<ApiResult<T>> =>
    new Promise((resolve) => {
      authApi.loadStoredTokens();
      const token = authApi.getAccessToken() || localStorage.getItem("access_token");
      sentToken = token;
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      Object.entries(localeHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = async () => {
        let body: any = null;
        try {
          body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
        } catch {
          body = xhr.responseText;
        }
        if ((xhr.status === 401 || xhr.status === 444) && !retried) {
          const r = await authApi.refreshAccessToken(sentToken);
          if (r.isSuccess) {
            resolve(attempt(true));
            return;
          }
        }
        if (xhr.status >= 200 && xhr.status < 300 && !(body && body.status === false)) {
          const data = body && typeof body === "object" && "data" in body ? body.data : body;
          resolve({ ok: true, status: xhr.status, data, raw: body });
        } else {
          resolve({ ok: false, status: xhr.status, error: parseError(xhr.status, body) });
        }
      };
      xhr.onerror = () => resolve({ ok: false, status: 0, error: { status: 0, message_code: "network.error" } });
      xhr.onabort = () => resolve({ ok: false, status: 0, error: { status: 0, message_code: "aborted" } });
      onAbortReady?.(() => xhr.abort());
      xhr.send(form);
    });
  return attempt(false);
}

/** Localized message for an API error: tError(message_code) → backend message → a specific fallback. */
export function useErrorText() {
  const { tError, tr, en } = useV2Lang();
  return useCallback(
    (err: ApiError | string | null | undefined, fallback?: string): string => {
      if (!err) return "";
      if (typeof err === "string") return err;
      if (err.message_code === "network.error") return tr("تعذّر الاتصال بالخادم. تحقق من الإنترنت ثم أعد المحاولة.", "Could not reach the server. Check your connection and retry.");
      if (err.status === 413 && !err.message_code) return uploadProblemText("too_large", en ? "en" : "ar");
      if (err.status === 415 && !err.message_code) return uploadProblemText("unsupported_type", en ? "en" : "ar");
      const localized = tError({ message_code: err.message_code, message: err.message, details: err.details } as any);
      if (localized && localized !== err.message_code) return localized;
      return err.message || fallback || tr("حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.", "Something unexpected happened. Please try again.");
    },
    [tError, tr, en]
  );
}
